<template>
  <teleport to="#modals-container">
    <TaskHandler
      :key="sidebarKey"
      :is-open="taskHandlerIsOpen"
      :task="taskHandlerData"
      @close-task-handler="taskHandlerToggle()"
      @new-task-submit="newTaskSubmit"
      @update-task-submit="updateTaskSubmit"
    />
  </teleport>

  <div v-if="taskStore.isLoading">
    <Skeleton
      width="full"
      height="40rem"
    />
  </div>
  <Card
    v-else
    class="h-full"
    full-height
    no-margin
  >
    <div
      class="todo-app h-full rounded-xl"
      style="min-height: calc(100vh - 150px)"
    >
      <!-- Menu -->
      <div
        class="todo-app-left"
        :class="{ open: menuOpen }"
      >
        <div class="p-5">
          <Button
            :label="$t('ui.actions.addItem', { item: $t('ui.entities.task.label') })"
            block
            icon="plus"
            @click="taskHandlerToggle()"
          />
        </div>
        <ul class="todo-app-tabs">
          <li
            v-for="tab in updatedTabs"
            :key="tab.name"
            class="todo-app-tab"
          >
            <router-link
              :to="tab.route"
              class="capitalize flex flex-row items-center"
              @click="menuOpen = false"
            >
              <Icon :type="tab.icon" />
              <div class="flex-1">{{ $t(tab.localeKey, 2) }}</div>
              <div class="flex gap-2">
                <Chip
                  v-if="tab.name !== 'Completed' && tab.name !== 'Deleted' && tab.overdueCount && tab.overdueCount > 0"
                  size="sm"
                  severity="danger"
                >
                  {{ tab.overdueCount }}
                </Chip>
                <Chip
                  v-if="
                    tab.name !== 'Completed' &&
                    tab.name !== 'Deleted' &&
                    tab.totalCount !== undefined &&
                    tab.totalCount > 0
                  "
                  size="sm"
                  severity="secondary"
                >
                  {{ tab.totalCount }}
                </Chip>
              </div>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="todo-app-right">
        <div class="todo-app-search-wrapper dark:border-darkmode-2">
          <Button
            icon="bars"
            class="todo-app-sidebar-toggle-btn md:hidden"
            plain
            severity="secondary"
            @click="toggleMenu"
          />
          <div class="flex items-center flex-1">
            <Input
              v-model="searchTerm"
              :placeholder="$t('ui.actions.searchItem', { item: _lowerCase($t('ui.entities.task.label', 2)) })"
              icon="search"
              icon-position="left"
              classes="flex-1 mr-3"
            />
          </div>

          <Dropdown text="Dropdown">
            <template #triggerContent>
              <Button
                icon="sort"
                plain
                severity="secondary"
                size="lg"
              />
            </template>
            <DropdownItem @item-clicked="changeSorting('titleAsc')">
              <Icon
                type="sort-alpha-down"
                class="mr-2"
              />
              Name (A to Z)
            </DropdownItem>
            <DropdownItem @item-clicked="changeSorting('titleDesc')">
              <Icon
                type="sort-alpha-up"
                class="mr-2"
              />
              Name (Z to A)
            </DropdownItem>
            <DropdownItem @item-clicked="changeSorting('dueDateAsc')">
              <Icon
                type="calendar-arrow-down"
                class="mr-2"
              />
              Due Date (Earliest)
            </DropdownItem>
            <DropdownItem @item-clicked="changeSorting('dueDateDesc')">
              <Icon
                type="calendar-arrow-up"
                class="mr-2"
              />
              Due Date (Latest)
            </DropdownItem>
          </Dropdown>
        </div>
        <ul class="todo-app-content">
          <TaskListItem
            v-for="task in filteredList"
            :key="task.id"
            :task="task"
            @task-clicked="taskHandlerToggle"
            @update-task="onTaskListItemChange"
          />
        </ul>
      </div>
    </div>
    <div
      class="todo-app-menu-overlay"
      :class="{ open: menuOpen }"
      @click="toggleMenu"
    ></div>
  </Card>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { computed, watch } from 'vue'

import { inject } from 'vue'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useTaskStore } from '@/stores/TaskStore.ts'

// components
import Button from '@/components/button/Button.vue'
import Card from '@/components/card/Card.vue'
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import TaskHandler from '@/components/tasks/TaskHandler.vue'
import TaskListItem from '@/components/tasks/TaskListItem.vue'
import Input from '@/components/forms/Input.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'

// composables
import { useAbility } from '@casl/vue'

// utilities
import _lowerCase from 'lodash/lowerCase'

const route = useRoute()
const authStore = useAuthStore()
const taskStore = useTaskStore()
const currentTab = ref(route.params.tab || 'all')
const searchTerm = ref('')
const sorting = ref(route.query.sort || 'dueDateAsc')
const menuOpen = ref(false)
const tasksTotalCounter = ref(0)
const { can } = useAbility()

const sidebarKey = ref(Math.random())

const emitter = inject('emitter')

const tabs = ref([
  {
    name: 'My Tasks',
    icon: 'home',
    route: '/tasks',
    isActive: false,
    localeKey: 'ui.entities.task.myTask'
  },
  {
    name: 'Important',
    icon: 'star',
    route: '/tasks/important',
    isActive: false,
    localeKey: 'ui.entities.task.important'
  },
  {
    name: 'Reminders',
    icon: 'bell',
    route: '/tasks/reminders',
    isActive: false,
    localeKey: 'ui.entities.task.reminder'
  },
  {
    name: 'Assigned to Me',
    icon: 'circle-down',
    route: '/tasks/assigned',
    isActive: false,
    localeKey: 'ui.entities.task.assignedToMe'
  },
  {
    name: 'Assigned by Me',
    icon: 'circle-right',
    route: '/tasks/assigned-by-me',
    isActive: false,
    localeKey: 'ui.entities.task.assignedByMe'
  },
  {
    name: 'Completed',
    icon: 'check',
    route: '/tasks/completed',
    isActive: false,
    localeKey: 'ui.entities.task.completed'
  },
  {
    name: 'Deleted',
    icon: 'trash',
    route: '/tasks/deleted',
    isActive: false,
    localeKey: 'ui.entities.task.deleted'
  }
])

// Fetch tasks from store on mount
taskStore.fetchTasks()

watch(
  () => tasksTotalCounter.value,
  () => {
    // console.log('totals changed: ' + tasksTotalCounter.value)
    emitter.emit('counter-tasks-total', tasksTotalCounter.value)
  }
)

function changeSorting(value) {
  sorting.value = value
}

watch(
  () => route.params.tab,
  newValue => {
    menuOpen.value = false
    if (newValue === '' || newValue === undefined) {
      currentTab.value = 'all'
    } else {
      currentTab.value = newValue
    }
    // console.log('Current Tab: ' + currentTab.value);
  },
  {
    immediate: true
  }
)

const filteredList = computed(() => {
  const searchTermLower = _lowerCase(searchTerm.value)

  // Common filter function
  const commonFilter = task =>
    _lowerCase(task.title).includes(searchTermLower) ||
    _lowerCase(task.linkTitle || '').includes(searchTermLower) ||
    _lowerCase(task.description || '').includes(searchTermLower)

  // Helper to get createdBy ID (handles both object and ID formats)
  const getCreatedById = task => {
    if (typeof task.createdBy === 'object' && task.createdBy !== null) {
      return task.createdBy.id
    }
    return task.createdBy
  }

  // Helper to get assignee ID from UserReference object
  const getAssigneeId = task => task.assignee.id

  // Tab specific filter conditions
  const tabConditions = {
    all: task =>
      !task.isDeleted && !task.isCompleted && !task.isReminder && getAssigneeId(task) == getCreatedById(task),
    important: task =>
      !task.isDeleted && !task.isCompleted && task.isStarred && getAssigneeId(task) == getCreatedById(task),
    assigned: task =>
      !task.isDeleted &&
      !task.isCompleted &&
      getAssigneeId(task) != getCreatedById(task) &&
      getCreatedById(task) != authStore.details.id,
    'assigned-by-me': task =>
      !task.isDeleted &&
      !task.isCompleted &&
      getAssigneeId(task) != getCreatedById(task) &&
      getCreatedById(task) == authStore.details.id,
    reminders: task =>
      !task.isDeleted && !task.isCompleted && task.isReminder && getAssigneeId(task) == authStore.details.id,
    completed: task => !task.isDeleted && task.isCompleted,
    deleted: task => task.isDeleted
  }

  // Filter tasks based on current tab and common filter
  const filtered = taskStore.tasks.filter(task => tabConditions[currentTab.value](task) && commonFilter(task))

  // Sorting logic
  const sortMapping = {
    titleDesc: (a, b) => (a.title < b.title ? 1 : -1),
    titleAsc: (a, b) => (a.title > b.title ? 1 : -1),
    dueDateDesc: (a, b) => {
      const dateA = a.dueDate || ''
      const dateB = b.dueDate || ''
      return dateA < dateB ? 1 : -1
    },
    dueDateAsc: (a, b) => {
      const dateA = a.dueDate || ''
      const dateB = b.dueDate || ''
      return dateA > dateB ? 1 : -1
    }
  }

  if (sortMapping[sorting.value]) {
    filtered.sort(sortMapping[sorting.value])
  }

  return filtered
})

const updatedTabs = computed(() => {
  const filtered = ref([])
  let totalsCounter = 0
  const now = new Date()

  // Helper function to count tasks based on due date
  const getTaskCounts = tasks => {
    const validTasks = tasks.filter(task => {
      const dueDate = task.dueDate && task.dueDate !== '0000-00-00 00:00:00'
      return dueDate && !isNaN(new Date(task.dueDate).getTime())
    })
    const future = validTasks.filter(task => new Date(task.dueDate) > now).length
    const overdue = validTasks.filter(task => new Date(task.dueDate) < now).length
    const total = tasks.length
    return { future, overdue, total }
  }

  // Helper to get createdBy ID (handles both object and ID formats)
  const getCreatedById = task => {
    if (typeof task.createdBy === 'object' && task.createdBy !== null) {
      return task.createdBy.id
    }
    return task.createdBy
  }

  // Helper to get assignee ID from UserReference object
  const getAssigneeId = task => task.assignee.id

  tabs.value.forEach(item => {
    if (item.name === 'My Tasks') {
      const filteredTasks = taskStore.tasks.filter(
        task => !task.isDeleted && !task.isCompleted && !task.isReminder && getAssigneeId(task) == getCreatedById(task)
      )
      const counts = getTaskCounts(filteredTasks)
      const tabIndex = tabs.value.findIndex(tab => tab.name === 'My Tasks')
      tabs.value[tabIndex].futureCount = counts.future
      tabs.value[tabIndex].overdueCount = counts.overdue
      tabs.value[tabIndex].totalCount = counts.total
      totalsCounter = totalsCounter + counts.total
    } else if (item.name === 'Important') {
      const filteredTasks = taskStore.tasks.filter(
        task => !task.isDeleted && !task.isCompleted && task.isStarred && getAssigneeId(task) == getCreatedById(task)
      )
      const counts = getTaskCounts(filteredTasks)
      const tabIndex = tabs.value.findIndex(tab => tab.name === 'Important')
      tabs.value[tabIndex].futureCount = counts.future
      tabs.value[tabIndex].overdueCount = counts.overdue
      tabs.value[tabIndex].totalCount = counts.total
    } else if (item.name === 'Reminders') {
      const filteredTasks = taskStore.tasks.filter(
        task => !task.isDeleted && !task.isCompleted && task.isReminder && getAssigneeId(task) == authStore.details.id
      )
      const counts = getTaskCounts(filteredTasks)
      const tabIndex = tabs.value.findIndex(tab => tab.name === 'Reminders')
      tabs.value[tabIndex].futureCount = counts.future
      tabs.value[tabIndex].overdueCount = counts.overdue
      tabs.value[tabIndex].totalCount = counts.total
      totalsCounter = totalsCounter + counts.total
    } else if (item.name === 'Assigned to Me') {
      const filteredTasks = taskStore.tasks.filter(
        task =>
          !task.isDeleted &&
          !task.isCompleted &&
          getAssigneeId(task) != getCreatedById(task) &&
          getCreatedById(task) != authStore.details.id
      )
      const counts = getTaskCounts(filteredTasks)
      const tabIndex = tabs.value.findIndex(tab => tab.name === 'Assigned to Me')
      tabs.value[tabIndex].futureCount = counts.future
      tabs.value[tabIndex].overdueCount = counts.overdue
      tabs.value[tabIndex].totalCount = counts.total
      totalsCounter = totalsCounter + counts.total
    } else if (item.name === 'Assigned by Me') {
      if (can('assign_tasks')) {
        const filteredTasks = taskStore.tasks.filter(
          task =>
            !task.isDeleted &&
            !task.isCompleted &&
            getAssigneeId(task) != getCreatedById(task) &&
            getCreatedById(task) == authStore.details.id
        )
        const counts = getTaskCounts(filteredTasks)
        const tabIndex = tabs.value.findIndex(tab => tab.name === 'Assigned by Me')
        tabs.value[tabIndex].futureCount = counts.future
        tabs.value[tabIndex].overdueCount = counts.overdue
        tabs.value[tabIndex].totalCount = counts.total
        totalsCounter = totalsCounter + counts.total
        item.isVisible = true
      } else {
        item.isVisible = false
      }
    } else if (item.name === 'Completed') {
      const filteredTasks = taskStore.tasks.filter(task => !task.isDeleted && task.isCompleted)
      const counts = getTaskCounts(filteredTasks)
      const tabIndex = tabs.value.findIndex(tab => tab.name === 'Completed')
      tabs.value[tabIndex].futureCount = counts.future
      tabs.value[tabIndex].overdueCount = counts.overdue
      tabs.value[tabIndex].totalCount = counts.total
    } else if (item.name === 'Deleted') {
      const filteredTasks = taskStore.tasks.filter(task => task.isDeleted)
      const counts = getTaskCounts(filteredTasks)
      const tabIndex = tabs.value.findIndex(tab => tab.name === 'Deleted')
      tabs.value[tabIndex].futureCount = counts.future
      tabs.value[tabIndex].overdueCount = counts.overdue
      tabs.value[tabIndex].totalCount = counts.total
    }
    tasksTotalCounter.value = totalsCounter
  })

  // Filter out any that have isVisible=false
  filtered.value = tabs.value.filter(tab => tab.isVisible != false)

  return filtered.value
})

// Toggle menu logic
function toggleMenu() {
  menuOpen.value = !menuOpen.value
  // console.log('menu open: ' + menuOpen.value)
}

// Task Handler sidebar
const taskHandlerIsOpen = ref(false)
const taskHandlerData = ref()

function taskHandlerToggle(task) {
  // console.log('TODO: ')
  // console.log(task)
  sidebarKey.value = Math.random() // Change key so sidebar will re-mount
  // console.log('DATA from Todo.vue ' + JSON.stringify(task))
  taskHandlerIsOpen.value = !taskHandlerIsOpen.value

  if (task) {
    // If update task
    taskHandlerData.value = task
  } else {
    // If New task
    taskHandlerData.value = null
  }
}
async function newTaskSubmit() {
  // Task is already added to store by TaskHandler, just close the handler
  taskHandlerToggle()
}

async function updateTaskSubmit() {
  // Task is already updated in store by TaskHandler, just close the handler
  taskHandlerToggle()
}

function onTaskListItemChange(task) {
  // Update task in store (for optimistic updates from TaskListItem)
  taskStore.updateTaskLocal(task)
}
</script>

<style>
.todo-app {
  display: flex;
  overflow: hidden;
  position: relative;
  height: calc(100vh - 80px);
}

.todo-app-left {
  width: 230px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-secondary);
  z-index: 1;
  transition: left 0.3s ease-in-out;
  border-radius: 0.428rem 0 0 0.428rem;
  min-height: 100%;
  background-color: var(--bg-primary_alt);
}

.todo-app-sidebar-toggle-btn {
  display: none;
}

.mobile .todo-app-left {
  position: absolute;
  left: -231px;
}

.mobile .todo-app-sidebar-toggle-btn {
  display: flex;
}

.mobile .todo-app-left.open {
  left: 0;
}

.todo-app-tab > a {
  padding: 10px;
  /* display: block; */
  font-weight: 500;
}

.todo-app-tab > a:hover {
  color: var(--fg-brand);
}

.todo-app-tab .icon {
  margin-left: 5px;
  margin-right: 10px;
}

.todo-app-tab > a.router-link-exact-active {
  color: var(--text-brand);
}

.todo-app-menu-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  border-radius: 0.428rem;
  background-color: #00000033;
  visibility: hidden;
  opacity: 0;

  transition: all 0.3s ease-in-out;
}

.todo-app-menu-overlay.open {
  visibility: visible;
  opacity: 1;
}

.todo-app-right {
  width: 100%;
  background-color: var(--bg-primary_alt);
  box-sizing: border-box;
  overflow-x: hidden;
}

.todo-app-content {
  margin: 0 var(--s-4) var(--s-4) var(--s-4);
  padding: 0;
  height: calc(100vh - 220px);
  overflow-y: auto;
  overflow-x: hidden;
}

.todo-app-search-wrapper {
  display: flex;
  padding: var(--s-3) var(--s-4);
  background-color: var(--bg-primary_alt);
  align-items: center;
}
</style>
